import { logger } from "@/utils/logger";

import React, { createContext, useContext, useEffect, useState } from "react";
import { isIOSDevice, isPwaMode } from "@/utils/pwaUtils";

// Simple detection context
interface TouchOffsetContextType {
  isIOSPwa: boolean;
  isReady: boolean;
}

const TouchOffsetContext = createContext<TouchOffsetContextType>({
  isIOSPwa: false,
  isReady: false
});

export const useTouchOffset = () => useContext(TouchOffsetContext);

// Initial fast detection
let isIOS = isIOSDevice();
let isPwa = isPwaMode();
let initialIOSPwa = isIOS && isPwa;

export const TouchOffsetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isIOSPwa, setIsIOSPwa] = useState(initialIOSPwa);
  const [isReady, setIsReady] = useState(false);
  
  // Re-evaluate on mount to ensure accurate detection after all components load
  useEffect(() => {
    // Verify iOS PWA status once DOM is fully loaded
    const verifyIOSPwa = () => {
      const confirmedIOS = isIOSDevice();
      const confirmedPwa = isPwaMode();
      const confirmedIOSPwa = confirmedIOS && confirmedPwa;
      
      if (confirmedIOSPwa !== isIOSPwa) {
        logger.info(`[TouchOffsetProvider] Updating iOS PWA status: ${confirmedIOSPwa}`);
        setIsIOSPwa(confirmedIOSPwa);
      }
      
      setIsReady(true);
    };
    
    // First check immediately
    verifyIOSPwa();
    
    // Then check again after a slight delay to ensure DOM is ready
    return () => {};
  }, []);
  
  return (
    <TouchOffsetContext.Provider
      value={{
        isIOSPwa,
        isReady
      }}
    >
      {children}
    </TouchOffsetContext.Provider>
  );
};
